import { useCallback, useEffect, useRef } from "react";
import { useJobPolling } from "./useJobPolling";
import { useJobs } from "../store/AppProvider";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";
import {
  NewsPreparationProgress,
  NewsRunSummary,
  PrepareCompanyNewsRequest,
  PublishedRange,
} from "../models/newsModels";
import { searchPeriodLabel } from "../components/common/NewsTimelineCard/utils";

type UseNewsPreparationParams = {
  compassCids: string[] | null;
  portfolioIndexIds: string[] | null;
  publishedRange?: PublishedRange | null;
  onResults: (searchedRange: PublishedRange | null) => void;
  onSearched?: () => void;
};

type UseNewsPreparationResult = {
  prepare: () => void;
  progress: NewsPreparationProgress | null;
};

export const useNewsPreparation = ({
  compassCids,
  portfolioIndexIds,
  publishedRange = null,
  onResults,
  onSearched,
}: UseNewsPreparationParams): UseNewsPreparationResult => {
  const { addToast } = useToast();
  const { addJob } = useJobs();
  const {
    startJob,
    jobId,
    progress,
    result,
    isPending,
    isRunning,
    isPolling,
    isComplete,
    isFailed,
  } = useJobPolling<NewsRunSummary>();
  const searchedRangeRef = useRef<PublishedRange | null>(null);
  const reportedStepRef = useRef(0);
  const announcedJobRef = useRef<string | null>(null);
  const finishedSteps = progress?.currentStep ?? 0;

  useEffect(() => {
    if (jobId) {
      addJob(jobId, MESSAGES.NEWS_FEED.JOB_NAME);
    }
  }, [jobId, addJob]);

  useEffect(() => {
    if (finishedSteps > reportedStepRef.current) {
      reportedStepRef.current = finishedSteps;
      onResults(searchedRangeRef.current);
    }
  }, [finishedSteps, onResults]);

  useEffect(() => {
    if (!isComplete || !jobId || announcedJobRef.current === jobId) {
      return;
    }
    announcedJobRef.current = jobId;
    const period = searchPeriodLabel(searchedRangeRef.current);
    const kept = result?.kept ?? 0;
    if (kept > 0) {
      addToast(MESSAGES.NEWS_FEED.STORIES_ADDED(kept, period), "success");
    } else {
      addToast(MESSAGES.NEWS_FEED.NOTHING_NEW(period), "info");
    }
    onSearched?.();
  }, [isComplete, jobId, result, addToast, onSearched]);

  useEffect(() => {
    if (isFailed) {
      addToast(MESSAGES.NEWS_FEED.PREPARE_FAILED, "error");
    }
  }, [isFailed, addToast]);

  const prepare = useCallback((): void => {
    searchedRangeRef.current = publishedRange;
    reportedStepRef.current = 0;
    const request: PrepareCompanyNewsRequest = {
      compass_cid: compassCids,
      portfolio_index_id: portfolioIndexIds,
      published_range: publishedRange,
    };
    startJob("AI_PREPARE_PORTFOLIO_NEWS", request);
  }, [compassCids, portfolioIndexIds, publishedRange, startJob]);

  const isPreparing = isPending || isRunning || isPolling;
  const preparationProgress: NewsPreparationProgress | null = isPreparing
    ? {
        current: finishedSteps,
        total: progress?.totalSteps ?? compassCids?.length ?? 0,
      }
    : null;

  return { prepare, progress: preparationProgress };
};
